import React, { use } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, Link } from '@mui/icons-material';
import Button from '@mui/material/Button';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Constants from 'style/constants';
import { v4 as uuidv4 } from 'uuid';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { IOType } from 'features/systems/domain/models/io-type';
import CreateDomipDeviceCommand from 'features/systems/domip/domain/models/create-domip-device-command';
import DomipProviderContext from 'features/systems/domip/providers/contexts/domip-provider-context';
import styles from './domip-outputs-component.module.scss';

const AmountOfDomipOutputs = 11;

const DomipOutputs = (): React.JSX.Element => {
  const { t } = useTranslation('domip');

  const { hasPermission } = usePermissionAuthorization();

  const {
    createDomipDevice,
    domipPulseNumberHasDevice,
    domipPulseNumberHasLinkedDevice,
    openDeviceDetails,
    getBreadCrumbsForDomipPulseNumber,
    getFunctionForDomipOutputPulseNumber,
  } = use(DomipProviderContext);

  const {
    systemsDetailsHook: { viewingMode, selectedSystem },
  } = useSystemsContextProvider();

  const textSx: SxProps<Theme> = {
    '&.Mui-disabled': {
      color: Constants.Colors.primary,
      opacity: 0.38,
    },
  };

  return (
    <div className={styles.outputs}>
      <Typography variant="h5">{t('outputs.title')}</Typography>
      {Array.from({ length: AmountOfDomipOutputs }, (_, index) => {
        const domipPulseNumber = index + 1;
        const functionForOutput = getFunctionForDomipOutputPulseNumber(domipPulseNumber);
        return (
          <div key={`output-${uuidv4()}`} className={styles.output}>
            <div className={styles.titleContainer}>
              <Typography
                variant="subtitle1"
                className={`${styles.outputLabel} ${viewingMode === 'viewing' && domipPulseNumberHasDevice(domipPulseNumber, true) ? styles.clickable : ''}`}
                onClick={() =>
                  viewingMode === 'viewing' &&
                  domipPulseNumberHasDevice(domipPulseNumber, true) &&
                  openDeviceDetails(domipPulseNumber, true)
                }
              >
                {t(`outputs.output`, { number: index + 1 })}
              </Typography>

              {domipPulseNumberHasLinkedDevice(domipPulseNumber, true) && (
                <div className={styles.linkButton}>
                  <BreadCrumbTooltip allElementNames={getBreadCrumbsForDomipPulseNumber(domipPulseNumber, true)}>
                    <Link sx={{ fontSize: 18 }} />
                  </BreadCrumbTooltip>
                </div>
              )}
            </div>

            {functionForOutput && (
              <div className={styles.functionContainer}>
                <Typography variant="subtitle1">
                  {t('outputs.function')}: {t(`outputs.functionType.${functionForOutput}`)}
                </Typography>
              </div>
            )}
            {!domipPulseNumberHasDevice(domipPulseNumber, true) && hasPermission(Permission.CreateDevice) && (
              <Button
                startIcon={<AddCircleOutlineRounded />}
                disabled={viewingMode === 'editing'}
                sx={textSx}
                onClick={() => {
                  if (selectedSystem?.id) {
                    const command = {
                      systemId: selectedSystem.id,
                      domipPulseNumber: index + 1,
                      ioType: IOType.output,
                    } as CreateDomipDeviceCommand;
                    createDomipDevice(command);
                  }
                }}
              >
                {t('outputs.createDevice')}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DomipOutputs;
